const LOCAL_ORIGINS = ["http://localhost:3000", "http://127.0.0.1:3000"];
const LOCAL_AUTH_API_ORIGINS = [
    "https://hk-server-stg-public-255631359878.europe-west6.run.app",
    "https://hk-server-stg-jampa-255631359878.europe-west6.run.app",
    "https://api-stg.hak.co.il",
    "https://jampa-api-stg.jampa.ai",
    "https://api.hak.co.il",
];
const BROWSER_AUTH_API_ORIGINS: Record<string, readonly string[]> = {
    "https://stg.hak.co.il": ["https://api-stg.hak.co.il", "https://hk-server-stg-public-255631359878.europe-west6.run.app"],
    "https://hk-stage-462507.web.app": ["https://api-stg.hak.co.il"],
    "https://hk-stage-462507.firebaseapp.com": ["https://api-stg.hak.co.il"],
    "https://stg.jampa.ai": ["https://jampa-api-stg.jampa.ai", "https://hk-server-stg-jampa-255631359878.europe-west6.run.app"],
    "https://admin.hak.co.il": ["https://api.hak.co.il", "https://hk-server-prod-public-730164058502.europe-west6.run.app"],
    "https://admin.jampa.ai": ["https://jampa-api.jampa.ai", "https://hk-server-prod-jampa-730164058502.europe-west6.run.app"],
};
type LoginFlow = "local" | "browser";
const STORAGE_PREFIX = "hk.local-stage-google.";
const MAX_AGE_MS = 10 * 60 * 1000;
const REQUEST_ID = /^[A-Za-z0-9_-]{32}$/;
const VERIFIER = /^[A-Za-z0-9_-]{43}$/;

export const LOCAL_AUTH_ERROR = "ההתחברות לא הושלמה. יש להתחיל שוב דרך כפתור הכניסה עם Google.";
const ROLE_FORBIDDEN_ERROR = "התחברות מקומית לסביבת הייצור מותרת למנהל מערכת ראשי (SUPER ADMIN) בלבד.";

type PendingLogin = {
    verifier: string;
    requestId: string;
    apiOrigin: string;
    returnOrigin: string;
    startedAt: number;
    flow?: LoginFlow;
};

export type LocalStageGoogleCallback = {
    status: string;
    requestId: string;
    code: string;
    handoff: string;
    error: string;
    valid: boolean;
    flow?: LoginFlow;
};

type Completion = {
    callback: LocalStageGoogleCallback;
    apiOrigin: string;
    returnOrigin: string;
    startedAt: number;
    promise: Promise<string>;
};

// StrictMode replays effects. A handoff must be exchanged only once, including
// when its response is uncertain; a retry starts a fresh Google login instead.
const completions = new Map<string, Completion>();

export function usesLocalStageGoogleAuth(apiOrigin: string, returnOrigin = window.location.origin): boolean {
    return LOCAL_ORIGINS.includes(returnOrigin) && LOCAL_AUTH_API_ORIGINS.includes(apiOrigin);
}

function supportsFlow(flow: LoginFlow, apiOrigin: string, returnOrigin: string): boolean {
    if (flow === "local") return usesLocalStageGoogleAuth(apiOrigin, returnOrigin);
    if (LOCAL_ORIGINS.includes(returnOrigin)) {
        return ["http://localhost:8080", "http://127.0.0.1:8080"].includes(apiOrigin);
    }
    return BROWSER_AUTH_API_ORIGINS[returnOrigin]?.includes(apiOrigin) === true;
}

function requireCrypto(): Crypto {
    if (!window.crypto?.getRandomValues || !window.crypto?.subtle) {
        throw new Error("הדפדפן אינו מאפשר התחברות מאובטחת. יש לפתוח את האתר בדפדפן מעודכן.");
    }
    return window.crypto;
}

function base64Url(bytes: Uint8Array): string {
    return window.btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(""))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function discardPending(requestId: string): void {
    if (!REQUEST_ID.test(requestId)) return;
    try {
        window.sessionStorage.removeItem(`${STORAGE_PREFIX}${requestId}`);
    } catch {
        // Storage may become unavailable while Google is open. Never log secrets.
    }
}

function removeExpiredPending(): void {
    const storage = window.sessionStorage;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index);
        if (!key?.startsWith(STORAGE_PREFIX)) continue;
        try {
            const pending = JSON.parse(storage.getItem(key) || "null");
            const age = Date.now() - pending?.startedAt;
            if (!Number.isFinite(age) || age < 0 || age >= MAX_AGE_MS) storage.removeItem(key);
        } catch {
            storage.removeItem(key);
        }
    }
    completions.forEach((completion, key) => {
        if (Date.now() - completion.startedAt >= MAX_AGE_MS) completions.delete(key);
    });
}

async function createGoogleLoginUrl(apiOrigin: string, flow: LoginFlow): Promise<string> {
    const returnOrigin = window.location.origin;
    if (!supportsFlow(flow, apiOrigin, returnOrigin)) throw new Error(LOCAL_AUTH_ERROR);
    const crypto = requireCrypto();
    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const requestId = base64Url(crypto.getRandomValues(new Uint8Array(24)));
    const challenge = base64Url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
    const pending: PendingLogin = {verifier, requestId, apiOrigin, returnOrigin, startedAt: Date.now(), flow};
    try {
        removeExpiredPending();
        const key = `${STORAGE_PREFIX}${requestId}`;
        const value = JSON.stringify(pending);
        window.sessionStorage.setItem(key, value);
        if (window.sessionStorage.getItem(key) !== value) throw new Error();
    } catch {
        discardPending(requestId);
        throw new Error("לא ניתן לשמור את בקשת ההתחברות בלשונית. יש לאפשר אחסון בדפדפן ולנסות שוב.");
    }
    const url = new URL(`${apiOrigin}/auth/google/${flow}`);
    url.searchParams.set("returnOrigin", returnOrigin);
    url.searchParams.set("codeChallenge", challenge);
    url.searchParams.set("requestId", requestId);
    return url.toString();
}

export function createLocalStageGoogleLoginUrl(apiOrigin: string): Promise<string> {
    return createGoogleLoginUrl(apiOrigin, "local");
}

export function createBrowserGoogleLoginUrl(apiOrigin: string): Promise<string> {
    return createGoogleLoginUrl(apiOrigin, "browser");
}

export function readLocalStageGoogleCallback(hash: string): LocalStageGoogleCallback | null {
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    if (!params.has("localAuth") && !params.has("browserAuth")) return null;
    const flow: LoginFlow = params.has("browserAuth") ? "browser" : "local";
    const marker = flow === "browser" ? "browserAuth" : "localAuth";
    return {
        flow,
        status: params.get(marker) || "",
        requestId: params.get("requestId") || "",
        code: params.get("code") || "",
        handoff: params.get("handoff") || "",
        error: params.get("error") || "",
        valid: !(params.has("localAuth") && params.has("browserAuth")) &&
            [marker, "requestId", "code", "handoff", "error"].every(key => params.getAll(key).length <= 1),
    };
}

export function completeLocalStageGoogleLogin(callback: LocalStageGoogleCallback, apiOrigin: string): Promise<string> {
    const returnOrigin = window.location.origin;
    const flow = callback.flow ?? "local";
    if (!supportsFlow(flow, apiOrigin, returnOrigin) || !callback.valid || !REQUEST_ID.test(callback.requestId)) {
        discardPending(callback.requestId);
        return Promise.reject(new Error(LOCAL_AUTH_ERROR));
    }
    const existing = completions.get(callback.requestId);
    if (existing) {
        if (existing.apiOrigin === apiOrigin && existing.returnOrigin === returnOrigin &&
            JSON.stringify(existing.callback) === JSON.stringify(callback) &&
            Date.now() - existing.startedAt < MAX_AGE_MS) return existing.promise;
        return Promise.reject(new Error(LOCAL_AUTH_ERROR));
    }

    const promise = (async () => {
        try {
            requireCrypto();
            const pending: PendingLogin | null = JSON.parse(window.sessionStorage.getItem(`${STORAGE_PREFIX}${callback.requestId}`) || "null");
            const age = Date.now() - (pending?.startedAt ?? NaN);
            if (!pending || pending.requestId !== callback.requestId || pending.apiOrigin !== apiOrigin ||
                (pending.flow ?? "local") !== flow ||
                pending.returnOrigin !== returnOrigin || !VERIFIER.test(pending.verifier) ||
                !Number.isFinite(age) || age < 0 || age >= MAX_AGE_MS) throw new Error();
            if (callback.status === "error" && callback.error === "access_denied") {
                throw new Error("הכניסה עם Google בוטלה. אפשר להתחיל שוב כשמתאים לך.");
            }
            if (callback.status !== "complete" || !callback.code || callback.code.length > 4096 ||
                !callback.handoff || callback.handoff.length > 8192) throw new Error();

            const controller = new AbortController();
            const timeout = window.setTimeout(() => controller.abort(), 30000);
            try {
                const response = await fetch(`${apiOrigin}/auth/google/${flow}/complete`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json", Accept: "application/json"},
                    credentials: "omit",
                    cache: "no-store",
                    redirect: "error",
                    signal: controller.signal,
                    body: JSON.stringify({
                        code: callback.code,
                        handoff: callback.handoff,
                        codeVerifier: pending.verifier,
                        requestId: callback.requestId,
                    }),
                });
                const result = await response.json();
                if (!response.ok) {
                    if (response.status === 403 && result?.code === "LOCAL_GOOGLE_ROLE_FORBIDDEN") {
                        throw new Error(ROLE_FORBIDDEN_ERROR);
                    }
                    throw new Error();
                }
                if (typeof result?.token !== "string" || !result.token) throw new Error();
                return result.token;
            } finally {
                window.clearTimeout(timeout);
            }
        } catch (error) {
            if (error instanceof Error && error.message === ROLE_FORBIDDEN_ERROR) throw error;
            if (callback.status === "error" && callback.error === "access_denied") {
                throw new Error("הכניסה עם Google בוטלה. אפשר להתחיל שוב כשמתאים לך.");
            }
            throw new Error(LOCAL_AUTH_ERROR);
        } finally {
            discardPending(callback.requestId);
        }
    })();
    completions.set(callback.requestId, {callback, apiOrigin, returnOrigin, startedAt: Date.now(), promise});
    return promise;
}
